import { useEffect, useCallback, useState } from 'react'
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { appApiBaseUrl } from '@/shared/api/app-client'
import type { Asset, LibraryCategory } from '@/shared/api/app-types'
import { cn } from '@/shared/ui/cn'

/** 将 /uploads/... 相对路径补全为后端完整 URL */
function resolveUrl(url: string | null): string | null {
  if (!url) return null
  if (url.startsWith('/uploads/')) {
    return `${appApiBaseUrl}${url}`
  }
  return url
}

/** 格式化文件大小 */
function formatFileSize(bytes: number | null): string {
  if (!bytes) return '-'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

/** 格式化时长（ms → mm:ss） */
function formatDuration(ms: number | null): string {
  if (!ms) return ''
  const totalSec = Math.floor(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

/** 触发下载（通过创建 a 标签，带 download 属性） */
function triggerDownload(url: string, fileName: string) {
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.target = '_blank'
  a.rel = 'noopener noreferrer'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

interface AssetPreviewDialogProps {
  /** 可预览的素材列表（同类型） */
  assets: Asset[]
  /** 当前预览的素材索引 */
  index: number
  /** 素材类型，决定预览方式 */
  category: LibraryCategory
  /** 切换索引 */
  onIndexChange: (index: number) => void
  /** 关闭 */
  onOpenChange: (open: boolean) => void
}

export function AssetPreviewDialog({
  assets,
  index,
  category,
  onIndexChange,
  onOpenChange,
}: AssetPreviewDialogProps) {
  const open = index >= 0 && index < assets.length
  const current = open ? assets[index] : null

  const goPrev = useCallback(() => {
    if (assets.length <= 1) return
    onIndexChange((index - 1 + assets.length) % assets.length)
  }, [assets.length, index, onIndexChange])

  const goNext = useCallback(() => {
    if (assets.length <= 1) return
    onIndexChange((index + 1) % assets.length)
  }, [assets.length, index, onIndexChange])

  // 键盘左右切换
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        goPrev()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        goNext()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, goPrev, goNext])

  if (!current) {
    return (
      <Dialog open={false} onOpenChange={onOpenChange}>
        <DialogContent className="hidden" />
      </Dialog>
    )
  }

  const publicUrl = resolveUrl(current.publicUrl)
  const thumbnailUrl = resolveUrl(current.thumbnailUrl)
  const hasMultiple = assets.length > 1

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        className="max-w-none gap-0 overflow-hidden border-none bg-black/90 p-0 sm:rounded-none"
        // 让弹窗铺满视口，用于媒体预览
        style={{ width: '100vw', height: '100vh', maxWidth: '100vw', left: '0', top: '0', transform: 'none', translate: 'none' }}
      >
          {/* 顶部工具栏 */}
          <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between px-4 py-3">
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate text-sm font-medium text-white">
                {current.title || current.fileName}
              </span>
              <span className="text-xs text-white/60">
                {formatFileSize(current.fileSize)}
                {current.durationMs ? ` · ${formatDuration(current.durationMs)}` : ''}
                {current.imageWidth && current.imageHeight
                  ? ` · ${current.imageWidth}×${current.imageHeight}`
                  : ''}
                {hasMultiple ? ` · ${index + 1} / ${assets.length}` : ''}
              </span>
            </div>
            <div className="flex items-center gap-1">
              {publicUrl && (
                <button
                  onClick={() => triggerDownload(publicUrl, current.fileName)}
                  className="flex size-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                  title="下载"
                >
                  <Download className="size-4" />
                </button>
              )}
              <button
                onClick={() => onOpenChange(false)}
                className="flex size-9 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                title="关闭"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>

          {/* 中间预览区 */}
          <div className="relative flex h-full w-full items-center justify-center">
            <PreviewBody
              category={category}
              asset={current}
              publicUrl={publicUrl}
              thumbnailUrl={thumbnailUrl}
            />

            {/* 左右切换按钮 */}
            {hasMultiple && (
              <>
                <button
                  onClick={goPrev}
                  className="absolute left-4 top-1/2 z-20 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                  title="上一个"
                >
                  <ChevronLeft className="size-6" />
                </button>
                <button
                  onClick={goNext}
                  className="absolute right-4 top-1/2 z-20 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                  title="下一个"
                >
                  <ChevronRight className="size-6" />
                </button>
              </>
            )}
          </div>

          {/* 底部缩略图条（多图时） */}
          {hasMultiple && (
            <div className="absolute inset-x-0 bottom-0 z-20 flex items-center justify-center gap-2 px-4 py-3">
              <div className="flex max-w-[80vw] items-center gap-1.5 overflow-x-auto rounded-full bg-white/5 px-3 py-1.5">
                {assets.map((a, i) => (
                  <button
                    key={a.id}
                    onClick={() => onIndexChange(i)}
                    className={cn(
                      'size-2 shrink-0 rounded-full transition-all',
                      i === index ? 'w-4 bg-white' : 'bg-white/40 hover:bg-white/60',
                    )}
                    title={a.fileName}
                  />
                ))}
              </div>
            </div>
          )}
        </DialogContent>
    </Dialog>
  )
}

/** 根据类型渲染预览内容 */
function PreviewBody({
  category,
  asset,
  publicUrl,
  thumbnailUrl,
}: {
  category: LibraryCategory
  asset: Asset
  publicUrl: string | null
  thumbnailUrl: string | null
}) {
  if (category === 'image' && publicUrl) {
    return (
      <img
        src={publicUrl}
        alt={asset.fileName}
        className="max-h-[85vh] max-w-[90vw] object-contain"
      />
    )
  }

  if (category === 'video' && publicUrl) {
    return (
      <video
        key={publicUrl}
        src={publicUrl}
        poster={thumbnailUrl ?? undefined}
        controls
        autoPlay
        className="max-h-[85vh] max-w-[90vw] object-contain"
      />
    )
  }

  if (category === 'audio' && publicUrl) {
    return (
      <div className="flex w-full max-w-md flex-col items-center gap-6 px-6">
        <div className="flex size-32 items-center justify-center rounded-full bg-white/10 text-white">
          {/* 简易音符图标，用 SVG 避免额外依赖 */}
          <svg viewBox="0 0 24 24" fill="currentColor" className="size-14">
            <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
          </svg>
        </div>
        <div className="text-center">
          <div className="text-base font-medium text-white">{asset.title || asset.fileName}</div>
          {asset.durationMs ? (
            <div className="mt-1 text-sm text-white/60">{formatDuration(asset.durationMs)}</div>
          ) : null}
        </div>
        <audio key={publicUrl} src={publicUrl} controls autoPlay className="w-full" />
      </div>
    )
  }

  if (category === 'font') {
    return <FontPreviewBody asset={asset} publicUrl={publicUrl} />
  }

  // 兜底
  return (
    <div className="flex flex-col items-center gap-3 text-white/60">
      <p className="text-sm">暂不支持预览此素材类型</p>
      {publicUrl && (
        <button
          onClick={() => triggerDownload(publicUrl, asset.fileName)}
          className="inline-flex h-9 items-center justify-center gap-2 rounded-full bg-white/10 px-4 text-xs font-medium text-white hover:bg-white/20"
        >
          <Download className="size-4" />
          下载文件
        </button>
      )}
    </div>
  )
}

/** 字体弹窗预览：动态加载字体后渲染多行大字样本 */
function FontPreviewBody({ asset, publicUrl }: { asset: Asset; publicUrl: string | null }) {
  const family = `fc-preview-${asset.id}`
  const [loaded, setLoaded] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!publicUrl) {
      setFailed(true)
      return
    }
    let cancelled = false
    const fontFace = new FontFace(family, `url("${publicUrl}")`)
    fontFace
      .load()
      .then(() => {
        if (!cancelled) {
          document.fonts.add(fontFace)
          setLoaded(true)
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
      document.fonts.delete(fontFace)
    }
  }, [family, publicUrl])

  const fontFamilyStyle = loaded ? { fontFamily: `'${family}', sans-serif` } : undefined
  const displayName = asset.fontFamilyName || asset.title || asset.fileName

  return (
    <div className="flex w-full max-w-xl flex-col items-center gap-8 px-6">
      <div className="text-center">
        <div className="text-lg font-medium text-white">{displayName}</div>
        <div className="mt-1 text-sm text-white/60">{asset.fileExt?.toUpperCase()} 字体文件</div>
      </div>

      {failed ? (
        <p className="text-center text-sm text-white/50">字体加载失败，可下载文件后本地查看。</p>
      ) : (
        <div className="w-full space-y-5 text-white" style={fontFamilyStyle}>
          <div className="text-center text-6xl font-medium leading-tight">永 Aa</div>
          <div className="space-y-2 text-center">
            <div className="text-2xl">创意无界 视频随心</div>
            <div className="text-base text-white/70">
              The quick brown fox jumps over the lazy dog.
            </div>
            <div className="text-sm text-white/50">0123456789 ABCDEFGHIJKLMN</div>
          </div>
        </div>
      )}

      {publicUrl && (
        <button
          onClick={() => triggerDownload(publicUrl, asset.fileName)}
          className="inline-flex h-9 items-center justify-center gap-2 rounded-full bg-white/10 px-4 text-xs font-medium text-white hover:bg-white/20"
        >
          <Download className="size-4" />
          下载字体
        </button>
      )}
    </div>
  )
}
